import React from 'react';
import { Bell, TrendingUp, Clock, CheckCircle2, Building2, Users, Activity } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { useAdminKPIMetrics } from '../../hooks/useAdminKPIMetrics';
import { SessionChart } from '../../components/admin/SessionChart';

interface KPICardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  trend?: {
    value: number;
    isPositive: boolean;
    label?: string;
  };
  className?: string;
}

function KPICard({ title, value, subtitle, icon, trend, className = '' }: KPICardProps) {
  return (
    <Card className={`bg-[#221F1F] border-[#504A4A] ${className}`}>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium text-[#A9A3A3] flex items-center gap-2">
          {icon}
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-3xl font-bold text-[#EBFFEE] mb-2">
          {typeof value === 'number' ? value.toLocaleString() : value}
        </div>
        {trend && (
          <div className={`text-sm flex items-center gap-1 ${trend.isPositive ? 'text-green-500' : 'text-red-500'}`}>
            <TrendingUp className={`w-4 h-4 ${!trend.isPositive ? 'rotate-180' : ''}`} />
            {trend.isPositive ? '+' : ''}{trend.value.toFixed(1)}%
            {trend.label && <span className="text-[#A9A3A3] ml-1">{trend.label}</span>}
          </div>
        )}
        {subtitle && (
          <div className="text-sm text-[#A9A3A3] mt-1">{subtitle}</div>
        )}
      </CardContent>
    </Card>
  );
}

export function KPIDashboard() {
  const { data: metrics, isLoading, error } = useAdminKPIMetrics();

  const formatTime = (seconds: number) => {
    if (seconds < 60) return `${Math.round(seconds)}s`;
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
    return `${Math.round(seconds / 3600)}h`;
  };

  if (error) {
    return (
      <div className="mb-4 p-4 bg-[#9B1421]/20 border border-[#9B1421] rounded-lg">
        <p className="text-rollops-accent text-sm">
          Error loading KPI metrics: {error instanceof Error ? error.message : 'Unknown error'}
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#9B1421]" />
        <span className="ml-3 text-[#A9A3A3]">Loading KPIs...</span>
      </div>
    );
  }

  const kpiMetrics = metrics || {
    total_companies: 0,
    total_users: 0,
    daily_active_users: 0,
    notification_open_rate: 0,
    notification_avg_time_to_open: 0,
    notification_action_completion_rate: 0,
    notification_unread_count: 0,
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {/* Total Companies KPI */}
      <KPICard
        title="Total Companies"
        value={kpiMetrics.total_companies.toLocaleString()}
        subtitle="Active gyms on platform"
        icon={<Building2 className="w-4 h-4" />}
      />

      {/* Total Users KPI */}
      <KPICard
        title="Total Users"
        value={kpiMetrics.total_users.toLocaleString()}
        subtitle="All registered users"
        icon={<Users className="w-4 h-4" />}
      />

      {/* Daily Active Users KPI */}
      <KPICard
        title="Daily Active Users"
        value={kpiMetrics.daily_active_users.toLocaleString()}
        subtitle="Users who logged in today"
        icon={<Activity className="w-4 h-4" />}
      />

      {/* Notification Open Rate KPI */}
      <KPICard
        title="Notification Open Rate"
        value={`${kpiMetrics.notification_open_rate.toFixed(1)}%`}
        subtitle="Last 30 days"
        icon={<Bell className="w-4 h-4" />}
        trend={
          kpiMetrics.notification_open_rate > 50
            ? { value: kpiMetrics.notification_open_rate, isPositive: true, label: 'target' }
            : kpiMetrics.notification_open_rate > 30
            ? { value: kpiMetrics.notification_open_rate, isPositive: true, label: 'good' }
            : { value: kpiMetrics.notification_open_rate, isPositive: false, label: 'low' }
        }
      />

      {/* Average Time to Open KPI */}
      <KPICard
        title="Avg Time to Open"
        value={formatTime(kpiMetrics.notification_avg_time_to_open)}
        subtitle="Time from creation to first open"
        icon={<Clock className="w-4 h-4" />}
      />

      {/* Action Completion Rate KPI */}
      <KPICard
        title="Action Completion Rate"
        value={`${kpiMetrics.notification_action_completion_rate.toFixed(1)}%`}
        subtitle="Notification actions completed"
        icon={<CheckCircle2 className="w-4 h-4" />}
        trend={
          kpiMetrics.notification_action_completion_rate > 70
            ? { value: kpiMetrics.notification_action_completion_rate, isPositive: true, label: 'excellent' }
            : kpiMetrics.notification_action_completion_rate > 50
            ? { value: kpiMetrics.notification_action_completion_rate, isPositive: true, label: 'good' }
            : { value: kpiMetrics.notification_action_completion_rate, isPositive: false, label: 'needs attention' }
        }
      />

      {/* Unread Notifications KPI */}
      <KPICard
        title="Unread Notifications"
        value={kpiMetrics.notification_unread_count.toLocaleString()}
        subtitle="Pending notifications"
        icon={<Bell className="w-4 h-4" />}
      />
    </div>
  );
}

export function KPIDashboardWithChart() {
  return (
    <div className="space-y-8">
      {/* KPI Cards */}
      <KPIDashboard />
      
      {/* Session Chart */}
      <SessionChart />
    </div>
  );
}

